import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Globe, Menu } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      // Navigate to home with hash; HomePage effect will scroll on arrival
      navigate(`/#${id}`);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white shadow-xl' : 'bg-white/95 backdrop-blur-md shadow-lg'
    }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/72qb31et_Better%20Than%20Yesterday%20%28Sweatshirts%29-2.png" 
              alt="PAW Padelarena Logo" 
              className="h-24 w-auto hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.about}
            </button>
            <Link to="/courts" className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover" data-testid="nav-courts-link">
              {t.nav.courts}
            </Link>
            <button onClick={() => scrollToSection('facilities')} className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.facilities}
            </button>
            <button onClick={() => scrollToSection('services')} className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.services}
            </button>
            <button onClick={() => scrollToSection('pricing')} className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.pricing}
            </button>
            <Link to="/academy" className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.academy}
            </Link>
            <Link to="/events" className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover" data-testid="nav-events-link">
              {t.nav.events}
            </Link>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors highlight-hover">
              {t.nav.contact}
            </button>
            
            {/* Language Selector */}
            <div className="relative group">
              <button className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors">
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs font-bold tracking-wider">{language}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute right-0 mt-2 w-28 bg-white rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden border border-slate-200">
                <button onClick={() => setLanguage('de')} className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-blue-600 transition-colors">
                  Deutsch
                </button>
                <button onClick={() => setLanguage('en')} className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-blue-600 transition-colors">
                  English
                </button>
                <button onClick={() => setLanguage('fr')} className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-blue-600 transition-colors">
                  Français
                </button>
                <button onClick={() => setLanguage('it')} className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-blue-600 transition-colors">
                  Italiano
                </button>
                <button onClick={() => setLanguage('es')} className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-blue-600 transition-colors">
                  Español
                </button>
              </div>
            </div>

            {/* CTA Button */}
            <a 
              href="https://bookpadelarena.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="athletic-button athletic-button-primary"
              data-testid="nav-book-now-btn"
            >
              {t.nav.bookNow}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-slate-900"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden glass border-t border-black/5"
        >
          <div className="px-6 py-6 space-y-4">
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium">
              {t.nav.about}
            </button>
            <Link to="/courts" className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="nav-courts-link-mobile">
              {t.nav.courts}
            </Link>
            <button onClick={() => scrollToSection('facilities')} className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium">
              {t.nav.facilities}
            </button>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium">
              {t.nav.services}
            </button>
            <button onClick={() => scrollToSection('pricing')} className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium">
              {t.nav.pricing}
            </button>
            <Link to="/academy" className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
              {t.nav.academy}
            </Link>
            <Link to="/events" className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="nav-events-link-mobile">
              {t.nav.events}
            </Link>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-slate-700 hover:text-slate-900 py-2 font-medium">
              {t.nav.contact}
            </button>
            
            <div className="flex gap-2 pt-2 border-t border-black/5">
              <button 
                onClick={() => {
                  setLanguage('de');
                  setMobileMenuOpen(false);
                }} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${language === 'de' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                DE
              </button>
              <button 
                onClick={() => {
                  setLanguage('en');
                  setMobileMenuOpen(false);
                }} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${language === 'en' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                EN
              </button>
              <button 
                onClick={() => {
                  setLanguage('fr');
                  setMobileMenuOpen(false);
                }} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${language === 'fr' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                FR
              </button>
              <button 
                onClick={() => {
                  setLanguage('it');
                  setMobileMenuOpen(false);
                }} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${language === 'it' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                IT
              </button>
              <button 
                onClick={() => {
                  setLanguage('es');
                  setMobileMenuOpen(false);
                }} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${language === 'es' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                ES
              </button>
            </div>
            
            <a 
              href="https://bookpadelarena.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="pill-button pill-button-primary w-full justify-center mt-4"
            >
              {t.nav.bookNow}
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
};
